import { useRef, useState, useEffect, SyntheticEvent } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/api/client';
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,

  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Switch,
  FormControlLabel,
  Skeleton,
  Divider,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  CameraAlt as CameraIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
// PageHelpIcon removed — provided by parent SettingsSection
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { TYPE } from '@/theme/typography';
import { useNavigate } from 'react-router-dom';
import { groupApi, CompanyInGroup, CompanyCreatePayload, GroupInfo } from '@/api/endpoints/group';
import { authApi } from '@/api/endpoints/auth';
import { settingsApi, Role } from '@/api/endpoints/settings';
import { adminRolesApi } from '@/api/endpoints/adminRoles';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import AddressForm from '@/components/common/AddressForm';
// Shared add/edit-company dialog — also used on the Group → Companies page.
import CompanyEditDialog from '@/components/group/CompanyFormDialog';
import PhoneInput from '@/components/common/PhoneInput';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { listAvailablePalettes, DEFAULT_PALETTE as _DEFAULT_PALETTE, getPaletteByKey } from '@/config/colorPalettes';
import { NEUTRAL_COLORS } from '@/theme/colorTokens';
import RichTextEditor from '@/components/common/RichTextEditor';
import apiClient from '@/api/client';
import { notificationsApi, BroadcastPayload } from '@/api/endpoints/notifications';
import SocialMediaFields from '@/components/common/SocialMediaFields';
import { formatDate, resolveUploadUrl } from '@/utils/format';
import { useUIStore } from '@/stores/uiStore';

// ────────────────────────────────────────────
// Tab Panel helper
// ────────────────────────────────────────────
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && children}
    </Box>
  );
}

// ────────────────────────────────────────────
// Role Permissions Panel
// ────────────────────────────────────────────
interface Permission {
  code: string;
  description?: string;
}

function RolePermissionsPanel({ role }: { role: Role }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  const { data: allPermissions = [], isLoading: permLoading } = useQuery({
    queryKey: ['settings', 'permissions'],
    queryFn: () => settingsApi.listPermissions().then((res) => res.data),
  });

  const { data: rolePermissions = [], isLoading: rolePermLoading } = useQuery({
    queryKey: ['settings', 'role-permissions', role.id],
    queryFn: () => settingsApi.getRolePermissions(role.id).then((res) => res.data),
    enabled: !role.is_system,
  });

  useEffect(() => {
    setSelectedPermissions(new Set(rolePermissions));
  }, [rolePermissions]);

  const permissionsByModule = allPermissions.reduce((acc: Record<string, Permission[]>, perm: any) => {
    const module = perm.code.split('.')[0];
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {});

  const moduleLabelMap: Record<string, string> = {
    accounting: 'Finance',
    hr: 'Human Resources',
    payroll: 'Payroll',
    tenants: 'Tenants',
    budget: 'Budget',
    group: 'Group',
    settings: 'Settings',
    audit: 'Audit',
    ai: 'AI',
    facility: 'Facility',
  };

  const updateMutation = useMutation({
    mutationFn: (codes: string[]) => adminRolesApi.replacePermissions(role.id, codes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'role-permissions', role.id] });
      showSuccess('Role permissions updated');
    },
    onError: (err: ApiError) => showError(err.response?.data?.detail || 'Failed to update permissions'),
  });

  if (permLoading || rolePermLoading) return <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>;

  if (role.is_system) {
    return (
      <Box sx={{ py: 3, px: 2 }}>
        <Alert severity="info" icon={<LockIcon />}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: TYPE.semibold }}>System Role — Permissions Locked</Typography>
          <Typography variant="body2">System roles cannot be modified.</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: TYPE.semibold, mb: 2 }}>Role Permissions</Typography>
      {Object.keys(permissionsByModule).sort().map((module) => (
        <Card key={module} sx={{ mb: 1.5 }}>
          <Box onClick={() => setExpandedModule(expandedModule === module ? null : module)} sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <Typography variant="subtitle2" fontWeight={TYPE.semibold}>{moduleLabelMap[module] || module}</Typography>
            {expandedModule === module ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={expandedModule === module}>
            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {permissionsByModule[module].map((perm: any) => (
                <Box key={perm.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <input type="checkbox" checked={selectedPermissions.has(perm.code)} onChange={() => {
                    const s = new Set(selectedPermissions);
                    if (s.has(perm.code)) {
                      s.delete(perm.code);
                    } else {
                      s.add(perm.code);
                    }
                    setSelectedPermissions(s);
                  }} style={{ cursor: 'pointer' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={TYPE.medium}>{perm.code}</Typography>
                    {perm.description && <Typography variant="caption" color="text.secondary">{perm.description}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Card>
      ))}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="outlined" size="small" disabled={updateMutation.isPending}>Cancel</Button>
        <Button variant="contained" size="small" onClick={() => updateMutation.mutate(Array.from(selectedPermissions))} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Permissions'}
        </Button>
      </Box>
    </Box>
  );
}

// Role Row
function RoleRow({ role }: { role: Role }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            <Typography variant="body2" fontWeight={TYPE.medium}>{role.name}</Typography>
          </Box>
        </TableCell>
        <TableCell><Typography variant="body2" color="text.secondary">{role.description || '--'}</Typography></TableCell>
        <TableCell><Chip label={role.is_system ? 'System' : 'Custom'} size="small" color={role.is_system ? 'primary' : 'default'} variant="outlined" /></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <RolePermissionsPanel role={role} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ────────────────────────────────────────────
// Announcement types
// ────────────────────────────────────────────
interface Announcement {
  id: string;
  group_id: string;
  title: string;
  body: string;
  is_active: boolean;
  is_pinned: boolean;
  priority?: string;
  posted_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface AnnouncementFormData {
  title: string;
  body: string;
  is_pinned: boolean;
  priority?: string;
}

// ────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────
export default function GroupSettingsPage({ embedded }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission: checkPerm, isSystemAdmin, isGroupAdmin } = usePermission();

  const hasPermission = isSystemAdmin || isGroupAdmin;

  const isGroupLevel = useAuthStore((s) => s.getActiveCompany()?.is_group_level ?? false);

  const { showSuccess, showError } = useNotificationStore();
  const fullPaletteMode = useUIStore((s) => s.fullPaletteMode);
  const setFullPaletteMode = useUIStore((s) => s.setFullPaletteMode);
  const sidebarStyle = useUIStore((s) => s.sidebarStyle);
  const setSidebarStyle = useUIStore((s) => s.setSidebarStyle);
  const [tab, setTab] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string }>({});
  const [deleteAnnouncementConfirmOpen, setDeleteAnnouncementConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<string | null>(null);

  // Group Info form
  const [groupForm, setGroupForm] = useState({
    name: '',
    address_line_1: '', address_line_2: '', city: '', lga: '', state: '', postal_code: '', country: 'NG',
    email: '',
    phone: '',
    website: '',
    description: '',
    logo_url: '',
    color_palette: 'etg',
    ceo_name: '',
    ceo_title: '',
    ceo_email: '',
    ceo_phone: '',
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    headquarters_company_id: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });

  // Company edit dialog
  const [companyDialog, setCompanyDialog] = useState<{ open: boolean; company: CompanyInGroup | null }>({ open: false, company: null });

  // ── Queries ──
  const { data: groupInfo, isLoading: groupLoading } = useQuery({
    queryKey: ['group', 'info'],
    queryFn: () => groupApi.getGroupInfo().then((res) => res.data),
    retry: 1,
  });

  const { data: groupCompanies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ['group', 'companies'],
    queryFn: () => groupApi.listCompanies(),
    select: (res) => { const d = res.data; return Array.isArray(d) ? d : []; },
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['settings', 'roles'],
    queryFn: () => settingsApi.listRoles(),
    select: (res) => { const d = res.data; return Array.isArray(d) ? d : []; },
  });

  useEffect(() => {
    if (groupInfo) {
      setGroupForm({
        name: groupInfo.name || '',
        address_line_1: groupInfo.address_line_1 || '', address_line_2: groupInfo.address_line_2 || '',
        city: groupInfo.city || '', lga: groupInfo.lga || '', state: groupInfo.state || '',
        postal_code: groupInfo.postal_code || '', country: groupInfo.country || '',
        email: groupInfo.email || '',
        phone: groupInfo.phone || '',
        website: groupInfo.website || '',
        description: groupInfo.description || '',
        logo_url: groupInfo.logo_url || '',
        color_palette: groupInfo.color_palette || 'etg',
        ceo_name: groupInfo.ceo_name || '',
        ceo_title: groupInfo.ceo_title || '',
        ceo_email: groupInfo.ceo_email || '',
        ceo_phone: groupInfo.ceo_phone || '',
        linkedin_url: groupInfo.linkedin_url || '',
        twitter_url: groupInfo.twitter_url || '',
        facebook_url: groupInfo.facebook_url || '',
        instagram_url: groupInfo.instagram_url || '',
        youtube_url: groupInfo.youtube_url || '',
        headquarters_company_id: groupInfo.headquarters_company_id || '',
        meta_title: (groupInfo as any).settings?.meta_title || groupInfo.name || '',
        meta_description: (groupInfo as any).settings?.meta_description || '',
        meta_keywords: (groupInfo as any).settings?.meta_keywords || '',
      });
    }
  }, [groupInfo]);

  // ── Mutations ──
  const updateGroupMutation = useMutation({
    mutationFn: (data: Partial<GroupInfo>) => groupApi.updateGroupInfo(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['group', 'info'] }); showSuccess('Group information updated'); },
    onError: (err: ApiError) => showError(err.response?.data?.detail || 'Failed to update group'),
  });

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompanyInGroup> }) => settingsApi.updateCompany(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['group', 'companies'] }); setCompanyDialog({ open: false, company: null }); showSuccess('Company updated'); },
    onError: (err: ApiError) => showError(err.response?.data?.detail || 'Failed to update company'),
  });

  const createCompanyMutation = useMutation({
    mutationFn: (data: CompanyCreatePayload) => groupApi.createCompany(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['group', 'companies'] });
      setCompanyDialog({ open: false, company: null });
      showSuccess('Company created — reference data and chart of accounts seeded');
      // Refresh auth state so the new company appears in the switcher
      try {
        const res = await authApi.getMe();
        useAuthStore.getState().setUser(res.data.user as any, res.data.companies);
      } catch { /* non-blocking */ }
    },
    onError: (err: ApiError) => showError(err.response?.data?.detail || 'Failed to create company'),
  });

  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoMutation = useMutation({
    mutationFn: (file: File) => groupApi.uploadGroupLogo(file),
    onSuccess: (res) => { showSuccess('Group logo updated'); queryClient.invalidateQueries({ queryKey: ['group', 'info'] }); setGroupForm((p) => ({ ...p, logo_url: res.data.logo_url })); },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to upload logo'),
  });

  const faviconInputRef = useRef<HTMLInputElement>(null);
  const faviconMutation = useMutation({
    mutationFn: (file: File) => groupApi.uploadGroupFavicon(file),
    onSuccess: () => { showSuccess('Favicon updated — hard refresh (Ctrl+Shift+R) to see it in browser tab'); queryClient.invalidateQueries({ queryKey: ['group', 'info'] }); },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to upload favicon'),
  });

  const [seoGenerating, setSeoGenerating] = useState(false);
  const handleGenerateSEO = async () => {
    setSeoGenerating(true);
    try {
      const res = await groupApi.generateSEO();
      setGroupForm((p) => ({
        ...p,
        meta_title: res.data.meta_title || p.meta_title,
        meta_description: res.data.meta_description || p.meta_description,
        meta_keywords: res.data.meta_keywords || p.meta_keywords,
      }));
      showSuccess(`SEO generated from ${res.data.companies_analyzed} company descriptions — review and save`);
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to generate SEO');
    } finally {
      setSeoGenerating(false);
    }
  };

  // ── Announcements ──
  const canWriteAnnouncements = checkPerm('portal.write');
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<string | null>(null);
  const [announcementForm, setAnnouncementForm] = useState<AnnouncementFormData>({ title: '', body: '', is_pinned: false, priority: 'medium' });
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastData, setBroadcastData] = useState<BroadcastPayload>({ title: '', body: '', send_email: false, company_only: true });

  const { data: announcementsData, isLoading: announcementsLoading } = useQuery<{ items: Announcement[]; total: number }>({
    queryKey: ['announcements'],
    queryFn: async () => (await apiClient.get<{ items: Announcement[]; total: number }>('/settings/announcements', { params: { active_only: false } })).data,
  });

  const createAnnouncementMutation = useMutation({
    mutationFn: (body: AnnouncementFormData) => apiClient.post('/settings/announcements', body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['announcements'] }); showSuccess('Announcement created'); setAnnouncementDialog(false); resetAnnouncementForm(); },
    onError: (err: ApiError) => showError(err.message || 'Failed to create announcement'),
  });

  const updateAnnouncementMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<AnnouncementFormData> }) => apiClient.patch(`/settings/announcements/${id}`, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['announcements'] }); showSuccess('Announcement updated'); setAnnouncementDialog(false); resetAnnouncementForm(); },
    onError: (err: ApiError) => showError(err.message || 'Failed to update announcement'),
  });

  const deleteAnnouncementMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/settings/announcements/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['announcements'] }); showSuccess('Announcement deleted'); },
    onError: (err: ApiError) => showError(err.message || 'Failed to delete'),
  });

  const broadcastMutation = useMutation({
    mutationFn: (data: BroadcastPayload) => notificationsApi.broadcast(data).then((res) => res.data),
    onSuccess: (result) => {
      const parts = [`${result.in_app_sent} in-app sent`];
      if (result.email_sent > 0) parts.push(`${result.email_sent} email(s)`);
      showSuccess(parts.join(', '));
      setBroadcastOpen(false);
      setBroadcastData({ title: '', body: '', send_email: false, company_only: true });
    },
    onError: (err: ApiError) => showError(err.response?.data?.detail || 'Failed to broadcast'),
  });

  const resetAnnouncementForm = () => { setAnnouncementForm({ title: '', body: '', is_pinned: false, priority: 'medium' }); setEditingAnnouncementId(null); };

  const announcementColumns: GridColDef[] = [
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 140 },
    { field: 'priority', headerName: 'Priority', flex: 0.4, minWidth: 80, renderCell: (p: any) => { const c: Record<string, 'default'|'warning'|'error'> = { high: 'warning', urgent: 'error' }; return <Chip label={p.value || 'medium'} size="small" color={c[p.value] || 'default'} />; } },
    { field: 'is_pinned', headerName: 'Pinned', flex: 0.3, minWidth: 60, valueGetter: (p: any) => p.value ? 'Yes' : 'No' },
    { field: 'is_active', headerName: 'Active', flex: 0.3, minWidth: 60, valueGetter: (p: any) => p.value ? 'Yes' : 'No' },
    { field: 'created_at', headerName: 'Created', flex: 0.5, minWidth: 90, valueGetter: (p: any) => formatDate(p.value) },
    {
      field: 'actions', headerName: '', type: 'actions', width: 80,
      getActions: (params: any) => canWriteAnnouncements ? [
        <GridActionsCellItem icon={<EditIcon fontSize="small" />} label="Edit" onClick={() => { setEditingAnnouncementId(params.row.id); setAnnouncementForm({ title: params.row.title, body: params.row.body, is_pinned: params.row.is_pinned, priority: params.row.priority }); setAnnouncementDialog(true); }} />,
        <GridActionsCellItem icon={<DeleteIcon fontSize="small" />} label="Delete" onClick={() => { setAnnouncementToDelete(params.row.id); setDeleteAnnouncementConfirmOpen(true); }} />,
      ] : [],
    },
  ];

  // ── Handlers ──
  const handleGroupFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGroupForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGroupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupForm.name.trim()) { setFieldErrors({ name: 'Group name is required' }); return; }
    setFieldErrors({});
    updateGroupMutation.mutate(groupForm);
  };

  // ── Guard ──
  if (!hasPermission) {
    return <Box sx={{ p: embedded ? 0 : 3 }}><Alert severity="error">Only System Administrators and Group Administrators can access group settings.</Alert></Box>;
  }

  if (groupLoading || companiesLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => logoInputRef.current?.click()}>
          <Avatar src={resolveUploadUrl(groupForm.logo_url)} variant="rounded" sx={{ width: 72, height: 72, bgcolor: 'primary.main' }}>
            <BusinessIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box sx={{ position: 'absolute', bottom: -4, right: -4, width: 26, height: 26, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'background.paper', '&:hover': { bgcolor: 'primary.dark' } }}>
            {logoMutation.isPending ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <CameraIcon sx={{ fontSize: TYPE.base }} />}
          </Box>
          <input ref={logoInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) logoMutation.mutate(f); e.target.value = ''; }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={TYPE.semibold}>{groupInfo?.name || 'Group'}</Typography>
          <Typography variant="body2" color="text.secondary">{groupCompanies.length} {groupCompanies.length === 1 ? 'company' : 'companies'}</Typography>
        </Box>
      </Stack>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(_: SyntheticEvent, v: number) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Profile" />
            <Tab label="Companies" />
            <Tab label="Roles & Permissions" />
            <Tab label="Announcements" />
          </Tabs>
        </Box>

        {/* ── Tab 0: Profile ── */}
        <TabPanel value={tab} index={0}>
          <CardContent>
            <form onSubmit={handleGroupSubmit}>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Group Name" name="name" value={groupForm.name} onChange={(e) => { handleGroupFormChange(e); setFieldErrors({}); }} required error={!!fieldErrors.name} helperText={fieldErrors.name} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Email" name="email" type="email" value={groupForm.email} onChange={handleGroupFormChange} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PhoneInput label="Phone" value={groupForm.phone} onChange={v => setGroupForm(prev => ({ ...prev, phone: v }))} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Website" name="website" value={groupForm.website} onChange={handleGroupFormChange} placeholder="https://www.example.com" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Description" name="description" value={groupForm.description} onChange={handleGroupFormChange} multiline rows={3} placeholder="Brief description of the group" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <AddressForm
                    values={{ address_line_1: groupForm.address_line_1, address_line_2: groupForm.address_line_2, city: groupForm.city, lga: groupForm.lga, state: groupForm.state, postal_code: groupForm.postal_code, country: groupForm.country }}
                    onChange={(field, value) => setGroupForm((prev) => ({ ...prev, [field]: value }))}
                    defaultCountryCode={groupForm.country}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={TYPE.semibold} sx={{ mb: 1, mt: 2 }}>Group Leadership</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="CEO / Chairman Name" name="ceo_name" value={groupForm.ceo_name} onChange={handleGroupFormChange} placeholder="e.g. John Doe" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Title" name="ceo_title" value={groupForm.ceo_title} onChange={handleGroupFormChange} placeholder="e.g. Group CEO, Chairman, President" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="CEO Email" name="ceo_email" type="email" value={groupForm.ceo_email} onChange={handleGroupFormChange} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PhoneInput label="CEO Phone" value={groupForm.ceo_phone} onChange={v => setGroupForm(prev => ({ ...prev, ceo_phone: v }))} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={TYPE.semibold} sx={{ mb: 1, mt: 2 }}>Social Media</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SocialMediaFields values={groupForm} onChange={handleGroupFormChange} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={TYPE.semibold} sx={{ mb: 1, mt: 2 }}>Branding</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Color Palette</InputLabel>
                    <Select
                      name="color_palette"
                      value={groupForm.color_palette}
                      onChange={(e) => setGroupForm((prev) => ({ ...prev, color_palette: e.target.value }))}
                      label="Color Palette"
                      renderValue={(selected) => {
                        const p = listAvailablePalettes().find((pl) => pl.key === selected);
                        if (!p) return selected;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              {[p.primary, p.secondary, p.success, p.warning, p.error, p.info].map((c, i) => (
                                <Box key={i} sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: c }} />
                              ))}
                            </Box>
                            <Typography variant="body2">{p.name}</Typography>
                          </Box>
                        );
                      }}
                    >
                      {listAvailablePalettes().map((palette) => (
                        <MenuItem key={palette.key} value={palette.key} sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" fontWeight={600}>{palette.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{palette.description}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {([
                                { color: palette.primary, label: 'Primary' },
                                { color: palette.primaryDark, label: 'Dark' },
                                { color: palette.primaryLight, label: 'Light' },
                                { color: palette.secondary, label: 'Secondary' },
                                { color: palette.success, label: 'Success' },
                                { color: palette.error, label: 'Error' },
                                { color: palette.warning, label: 'Warning' },
                                { color: palette.info, label: 'Info' },
                              ] as const).map((swatch, i) => (
                                <Tooltip key={i} title={`${swatch.label}: ${swatch.color}`} arrow>
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '6px',
                                      backgroundColor: swatch.color,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      transition: 'transform 0.15s',
                                      '&:hover': { transform: 'scale(1.15)' },
                                    }}
                                  />
                                </Tooltip>
                              ))}
                              <Tooltip title={`Background: ${palette.background}`} arrow>
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '6px',
                                    backgroundColor: palette.background,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'transform 0.15s',
                                    '&:hover': { transform: 'scale(1.15)' },
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontSize: TYPE.xs, mb: 0.5 }}>
                      Palette Application Mode
                    </FormLabel>
                    <RadioGroup
                      row
                      value={fullPaletteMode ? 'full' : 'balanced'}
                      onChange={(e) => setFullPaletteMode(e.target.value === 'full')}
                    >
                      <FormControlLabel
                        value="balanced"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={TYPE.semibold}>Balanced</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Palette on accents only, UI/UX guidelines respected
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mr: 3 }}
                      />
                      <FormControlLabel
                        value="full"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={TYPE.semibold}>Full Palette</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Palette colors on all decorative elements
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {/* Sidebar Style */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontSize: TYPE.xs, mb: 1 }}>
                      Sidebar Style
                    </FormLabel>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {([
                        { key: 'light' as const, label: 'Light', bg: NEUTRAL_COLORS.white, text: NEUTRAL_COLORS.textDark },
                        { key: 'colored' as const, label: 'Colored', bg: getPaletteByKey(groupForm.color_palette).primaryDark, text: NEUTRAL_COLORS.white },
                        { key: 'dark' as const, label: 'Dark', bg: NEUTRAL_COLORS.paperDark, text: NEUTRAL_COLORS.textInverted },
                      ]).map((opt) => {
                        const selected = sidebarStyle === opt.key;
                        return (
                          <Box
                            key={opt.key}
                            onClick={() => setSidebarStyle(opt.key)}
                            sx={{
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: selected ? 'primary.main' : 'divider',
                              borderRadius: 1.5,
                              p: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5,
                              transition: 'border-color 0.2s',
                              '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary' },
                            }}
                          >
                            {/* Mini sidebar preview */}
                            <Box
                              sx={{
                                width: 48,
                                height: 64,
                                bgcolor: opt.bg,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                flexDirection: 'column',
                                p: 0.5,
                                gap: 0.5,
                              }}
                            >
                              {[0, 1, 2, 3].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    height: 4,
                                    borderRadius: 0.5,
                                    bgcolor: i === 1 ? (opt.key === 'light' ? getPaletteByKey(groupForm.color_palette).primary : 'rgba(255,255,255,0.5)') : `${opt.text}40`,
                                    width: i === 1 ? '100%' : '70%',
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography variant="caption" fontWeight={selected ? TYPE.bold : TYPE.medium} color={selected ? 'primary.main' : 'text.secondary'}>
                              {opt.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </FormControl>
                </Grid>
                {/* Headquarters Company */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small">
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: TYPE.sm }}>
                      Headquarters Company
                    </Typography>
                    <Select
                      value={groupForm.headquarters_company_id || ''}
                      onChange={(e) => setGroupForm((p) => ({ ...p, headquarters_company_id: e.target.value }))}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {groupCompanies.map((c: any) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      The default company shown in the switcher for system and group administrators.
                    </Typography>
                  </FormControl>
                </Grid>
                {/* ── Favicon Upload ── */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={TYPE.semibold} sx={{ mb: 1, mt: 2 }}>Platform Favicon</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    The small icon shown in browser tabs. Upload a square image (PNG, JPG, or WebP). Recommended 128x128px or larger.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{ cursor: 'pointer', position: 'relative' }}
                      onClick={() => faviconInputRef.current?.click()}
                    >
                      <Avatar
                        src={(groupInfo as any)?.settings?.favicon_url ? `/uploads${(groupInfo as any).settings.favicon_url.replace('/uploads', '')}` : '/favicon.svg'}
                        variant="rounded"
                        sx={{ width: 48, height: 48, bgcolor: 'primary.main', border: '2px solid', borderColor: 'divider' }}
                      >
                        {(groupInfo?.name || 'A').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ position: 'absolute', bottom: -4, right: -4, width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'background.paper' }}>
                        {faviconMutation.isPending ? <CircularProgress size={10} sx={{ color: 'white' }} /> : <CameraIcon sx={{ fontSize: 10 }} />}
                      </Box>
                      <input ref={faviconInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) faviconMutation.mutate(f); e.target.value = ''; }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={TYPE.medium}>Current Favicon</Typography>
                      <Typography variant="caption" color="text.secondary">Click to upload a new one</Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* ── SEO & Meta Tags ── */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={TYPE.semibold}>SEO & Meta Tags</Typography>
                      <Typography variant="caption" color="text.secondary">
                        One platform, one brand. Browser tab title, search results, and Open Graph sharing.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleGenerateSEO}
                      disabled={seoGenerating}
                      startIcon={seoGenerating ? <CircularProgress size={14} /> : <AddIcon />}
                    >
                      {seoGenerating ? 'Generating...' : 'AI Generate'}
                    </Button>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Platform Title" name="meta_title" value={groupForm.meta_title} onChange={handleGroupFormChange} placeholder="e.g. Executive Talents Group ERP" helperText={`${(groupForm.meta_title || '').length}/70 — Appears in browser tabs and search results`} inputProps={{ maxLength: 70 }} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Meta Description" name="meta_description" value={groupForm.meta_description} onChange={handleGroupFormChange} multiline rows={2} placeholder="Multi-company HR, Accounting & Finance platform" helperText={`${(groupForm.meta_description || '').length}/200 — Shown in search result snippets`} inputProps={{ maxLength: 200 }} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Meta Keywords" name="meta_keywords" value={groupForm.meta_keywords} onChange={handleGroupFormChange} placeholder="e.g. ERP, HR management, payroll, accounting" helperText="Comma-separated keywords for search engines" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button variant="contained" type="submit" disabled={updateGroupMutation.isPending}>
                    {updateGroupMutation.isPending ? 'Saving...' : 'Save Group Info'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </TabPanel>

        {/* ── Tab 1: Companies ── */}
        <TabPanel value={tab} index={1}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Subsidiary Companies</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCompanyDialog({ open: true, company: null })}>
                  Add Company
                </Button>
                <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate('/group/companies')}>
                  Manage in Group
                </Button>
              </Stack>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Short Code</TableCell>
                    <TableCell>CEO / MD</TableCell>
                    <TableCell>Colour</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell width={70}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupCompanies?.map((company: CompanyInGroup) => (
                    <TableRow key={company.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={TYPE.medium}>{company.name}</Typography></TableCell>
                      <TableCell><Chip label={company.short_code} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        {company.ceo_name ? (
                          <Box>
                            <Typography variant="body2" fontWeight={TYPE.medium}>{company.ceo_name}</Typography>
                            {company.ceo_title && <Typography variant="caption" color="text.secondary">{company.ceo_title}</Typography>}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">Not set</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '4px', backgroundColor: company.colour || 'grey.400', border: '1px solid', borderColor: 'divider' }} />
                          <Typography variant="caption" color="text.secondary">{company.colour || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={company.is_active ? 'Active' : 'Inactive'} color={company.is_active ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        <Tooltip title="Edit Company">
                          <IconButton size="small" onClick={() => setCompanyDialog({ open: true, company })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!groupCompanies || groupCompanies.length === 0) && (
                    <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No companies found.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </TabPanel>

        {/* ── Tab 2: Roles & Permissions ── */}
        <TabPanel value={tab} index={2}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Manage Roles & Permissions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click any role to view and edit its permissions. System roles are locked.
            </Typography>
            {rolesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
            ) : (
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell width={120}>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles?.map((role: Role) => <RoleRow key={role.id} role={role} />)}
                    {(!roles || roles.length === 0) && (
                      <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No roles found.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* ── Tab 3: Announcements ── */}
        <TabPanel value={tab} index={3}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={TYPE.semibold}>Announcements</Typography>
                <Typography variant="body2" color="text.secondary">Create and manage announcements visible to all employees.</Typography>
              </Box>
              {canWriteAnnouncements && (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="secondary" size="small" startIcon={<CampaignIcon />} onClick={() => setBroadcastOpen(true)}>
                    Notify All Staff
                  </Button>
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { resetAnnouncementForm(); setAnnouncementDialog(true); }}>
                    New Announcement
                  </Button>
                </Stack>
              )}
            </Stack>
            <Box>
            {announcementsLoading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <DataGrid
                autoHeight
                density="compact"
                rows={announcementsData?.items || []}
                columns={announcementColumns}
                pageSizeOptions={[10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                disableRowSelectionOnClick
              />
            )}
          </Box>
          </CardContent>
        </TabPanel>

      </Card>

      {/* Company Edit Dialog */}
      <CompanyEditDialog
        open={companyDialog.open}
        company={companyDialog.company}
        onClose={() => setCompanyDialog({ open: false, company: null })}
        onSave={(id, data) => updateCompanyMutation.mutate({ id, data })}
        onCreate={(data) => createCompanyMutation.mutate({ name: data.name, country_code: data.country_code || data.country, ...data } as CompanyCreatePayload)}
        saving={updateCompanyMutation.isPending || createCompanyMutation.isPending}
      />

      {/* Announcement Dialog */}
      <Dialog open={announcementDialog} onClose={() => setAnnouncementDialog(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>{editingAnnouncementId ? 'Edit Announcement' : 'New Announcement'}</BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField label="Title" value={announcementForm.title} onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })} fullWidth required />
            <RichTextEditor label="Body" value={announcementForm.body} onChange={(html) => setAnnouncementForm({ ...announcementForm, body: html })} placeholder="Write your announcement..." minHeight={120} />
            <TextField label="Priority" select value={announcementForm.priority || 'medium'} onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: e.target.value })} SelectProps={{ native: true }} fullWidth>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </TextField>
            <FormControlLabel control={<Switch checked={announcementForm.is_pinned} onChange={(e) => setAnnouncementForm({ ...announcementForm, is_pinned: e.target.checked })} />} label="Pin to top" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnouncementDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={createAnnouncementMutation.isPending || updateAnnouncementMutation.isPending} onClick={() => {
            if (!announcementForm.title.trim() || !announcementForm.body.trim()) { showError('Fill in all fields'); return; }
            if (editingAnnouncementId) {
              updateAnnouncementMutation.mutate({ id: editingAnnouncementId, body: announcementForm });
            } else {
              createAnnouncementMutation.mutate(announcementForm);
            }
          }}>
            {editingAnnouncementId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>Notify All Staff</BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Send an in-app notification to every active staff member.</Typography>
          <Stack spacing={2}>
            <TextField label="Title" value={broadcastData.title} onChange={(e) => setBroadcastData({ ...broadcastData, title: e.target.value })} fullWidth required placeholder="e.g. Company Town Hall — Friday 3pm" />
            <RichTextEditor label="Message" value={broadcastData.body} onChange={(html) => setBroadcastData({ ...broadcastData, body: html })} placeholder="Write your message..." minHeight={120} />
            <FormControlLabel control={<Switch checked={broadcastData.send_email} onChange={(e) => setBroadcastData({ ...broadcastData, send_email: e.target.checked })} />} label="Also send via email" />
            {isGroupLevel && (
              <FormControlLabel control={<Switch checked={!broadcastData.company_only} onChange={(e) => setBroadcastData({ ...broadcastData, company_only: !e.target.checked })} />} label="Send to all group companies" />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" startIcon={<CampaignIcon />} disabled={broadcastMutation.isPending} onClick={() => { if (!broadcastData.title.trim() || !broadcastData.body.trim()) { showError('Fill in title and message'); return; } broadcastMutation.mutate(broadcastData); }}>
            {broadcastMutation.isPending ? 'Sending...' : 'Send to All Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteAnnouncementConfirmOpen}
        title="Delete Announcement"
        description="Delete this announcement? It will be removed immediately and staff will no longer see it."
        confirmLabel="Delete"
        onConfirm={() => { if (announcementToDelete) deleteAnnouncementMutation.mutate(announcementToDelete); }}
        onClose={() => { setDeleteAnnouncementConfirmOpen(false); setAnnouncementToDelete(null); }}
      />
    </Box>
  );
}
